<template>
    <div>
        <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">{{ $t('hours') }}</h6>
            <b-button variant="success" size="sm" @click="showAddForm" v-if="!isAdding">
                <i class="ri-add-line"></i> {{ $t('add_new') }}
            </b-button>
        </div>

        <div v-if="isAdding" class="mb-3 p-3 border rounded border-success">
            <ValidationObserver ref="addFormValidate">
                <b-row class="align-items-center">
                    <b-col md="4">
                        <ValidationProvider name="day" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('day')">
                                <days-selectbox :validateError="errors[0]" v-model="addForm.day" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                    <b-col md="4">
                        <ValidationProvider name="start_time" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('start_time')">
                                <b-form-input type="time" :state="errors[0] ? false : null" v-model="addForm.start_time"></b-form-input>
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                    <b-col md="4">
                        <ValidationProvider name="end_time" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('end_time')">
                                <b-form-input type="time" :state="errors[0] ? false : null" v-model="addForm.end_time"></b-form-input>
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                </b-row>
                <div class="mt-3 d-flex justify-content-end">
                    <b-button variant="secondary" size="sm" class="mr-2" @click="cancelAdd">{{ $t('cancel') }}</b-button>
                    <b-button variant="success" size="sm" @click="confirmAdd" :disabled="formLoading">
                        <b-spinner small v-if="formLoading"></b-spinner> {{ $t('add') }}
                    </b-button>
                </div>
            </ValidationObserver>
        </div>

        <div class="mb-3">
            <datatable-filter @filter="filterData" @filterClear="filterClearData" :isTitle="false">
                <b-row>
                    <b-col md="6">
                        <b-form-group :label="$t('day')">
                            <days-selectbox v-model="filterParams.day" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>
        </div>

        <div>
            <datatable :isLoading.sync="formLoading" :columns="tableColumns" :rows="formattedHours"
                       :showPaginate="false" :showTable="true" :title="''">
            </datatable>
        </div>
    </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate"
import LibraryRoomHourService from "@/services/LibraryRoomHourService"
import Datatable from "@/components/datatable/Datatable"
import DatatableFilter from "@/components/datatable/DatatableFilter"
import DaysSelectbox from "@/components/interactive-fields/DaysSelectbox.vue"

export default {
    components: {
        ValidationProvider, ValidationObserver, Datatable, DatatableFilter, DaysSelectbox
    },
    props: {
        formId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            formLoading: false,
            isAdding: false,
            filterParams: {
                day: null
            },
            appliedFilterParams: {
                day: null
            },
            form: {
                hours: []
            },
            addForm: {
                day: null,
                start_time: '',
                end_time: ''
            },
            dayOptions: [
                { value: 1, text: this.$t('monday') },
                { value: 2, text: this.$t('tuesday') },
                { value: 3, text: this.$t('wednesday') },
                { value: 4, text: this.$t('thursday') },
                { value: 5, text: this.$t('friday') },
                { value: 6, text: this.$t('saturday') },
                { value: 7, text: this.$t('sunday') }
            ],
            tableColumns: [
                 {
                    label: '',
                    field: 'buttons',
                    html: true,
                    sortable: false,
                    tdClass: 'p-0 text-center w-40 align-middle',
                    buttons: [
                        {
                            text: this.$t('delete'),
                            class: 'ri-delete-bin-2-line',
                            callback: (row) => {
                                this.removeRow(row._id);
                            }
                        }
                    ]
                },
                {
                    label: this.$t('day'),
                    field: 'dayText',
                    sortable: false
                },
                {
                    label: this.$t('start_time'),
                    field: 'start_time',
                    sortable: false
                },
                {
                    label: this.$t('end_time'),
                    field: 'end_time',
                    sortable: false
                },
               
            ]
        }
    },
    computed: {
        formattedHours() {
            let filtered = this.form.hours;
            if (this.appliedFilterParams.day) {
                filtered = filtered.filter(item => item.day == this.appliedFilterParams.day);
            }
            return filtered.map(item => ({
                ...item,
                dayText: this.getDayText(item.day)
            }));
        }
    },
    mounted() {
        this.loadData();
    },
    methods: {
        filterData() {
            this.appliedFilterParams = { ...this.filterParams };
        },
        filterClearData() {
            this.filterParams.day = null;
            this.appliedFilterParams.day = null;
        },
        getDayText(dayVal) {
            const dayOpt = this.dayOptions.find(d => d.value == dayVal);
            return dayOpt ? dayOpt.text : dayVal;
        },
        generateId() {
            return Math.random().toString(36).substr(2, 9);
        },
        loadData() {
            this.formLoading = true;
            LibraryRoomHourService.getAll({ params: { "filter[library_room_id]": this.formId } })
                .then(response => {
                    if (response.data.data && Array.isArray(response.data.data)) {
                        this.form.hours = response.data.data.map(item => {
                            let start_time = item.start_time ? item.start_time.substring(0, 5) : '';
                            let end_time = item.end_time ? item.end_time.substring(0, 5) : '';
                            return {
                                _id: this.generateId(),
                                day: item.day,
                                start_time: start_time,
                                end_time: end_time
                            }
                        });
                    }
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.formLoading = false;
                });
        },
        showAddForm() {
            this.addForm = { day: null, start_time: '', end_time: '' };
            this.isAdding = true;
            // Clear previous validations if exist
            this.$nextTick(() => {
                if (this.$refs.addFormValidate) {
                    this.$refs.addFormValidate.reset();
                }
            });
        },
        cancelAdd() {
            this.isAdding = false;
        },
        async confirmAdd() {
            const isValid = await this.$refs.addFormValidate.validate();
            if (isValid) {
                this.form.hours.push({
                    _id: this.generateId(),
                    day: this.addForm.day,
                    start_time: this.addForm.start_time,
                    end_time: this.addForm.end_time
                });
                this.isAdding = false;
                this.syncData();
            }
        },
        removeRow(id) {
            this.form.hours = this.form.hours.filter(item => item._id !== id);
            this.syncData();
        },
        syncData() {
            this.formLoading = true;
            
            const hoursPayload = this.form.hours.map(item => ({
                day: item.day,
                start_time: item.start_time,
                end_time: item.end_time
            }));

            const dataToSave = {
                library_room_id: this.formId,
                hours: hoursPayload
            };

            LibraryRoomHourService.store(dataToSave)
                .then((response) => {
                    this.$toast.success(this.$t("api." + response.data.message));
                    // Verileri API'den tekrar çek ve modal kapanmasın
                    this.loadData();
                })
                .catch((error) => {
                    if (error.response && error.response.data && error.response.data.message) {
                        this.$toast.error(error.response.data.message);
                    } else {
                        console.error(error);
                    }
                    this.formLoading = false;
                });
        }
    }
}
</script>
